import { css } from '@emotion/react'
import { Alert, Cell, FormControl, Grid, Heading, HFlow, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { isEstagio } from 'components/auth/useSessionUtils'
import { useErrorHandler } from 'components/error'
import {
  CheckboxField,
  DateField,
  DefaultFormFooter,
  ErrorField,
  Form,
  FormDebouncedValueSpy,
  FormPrompt,
  FormProps,
  FormRenderProps,
  NumberField,
  RichTextField,
} from 'components/form'
import { LotacaoEditableTable } from 'components/form/editable-table/LotacaoEditableTable'
import { ProcedimentoSelectField } from 'components/form/field/select/ProcedimentoSelectField'
import { RecordStringSelectField } from 'components/form/final-form/RecordStringSelectField/RecordStringSelectField'
import { ATIVIDADE_COLETIVA_ATIVIDADE_SAUDE_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { LotacaoResponsavelSection } from 'components/lotacao-responsavel/LotacaoResponsavelSection'
import { useFlags } from 'config/useFlagsContext'
import { subMonths } from 'date-fns'
import { FormState } from 'final-form'
import createDecorator from 'final-form-calculate'
import { useSalvarAtividadeColetivaMutation } from 'graphql/hooks.generated'
import { TipoGrupoParticipanteAtividadeColetivaEnum, TipoProcedimentoEnum } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useServerTime } from 'hooks/useServerTime'
import { Dispatch, SetStateAction, useEffect } from 'react'
import { Redirect, useHistory } from 'react-router'
import { turnoRecord } from 'types/enums'
import { dateAsYyyyMmDd } from 'util/date/formatDate'
import { metaPath } from 'util/metaPath'

import { AtividadeColetivaGrupoItemModel } from '../componentes/AtividadeColetivaGrupoSelectField'
import AtividadeColetivaGruposField from '../componentes/AtividadeColetivaGruposField'
import { LocalAtividadeField } from '../componentes/LocalAtividadeField'
import { ParticipanteEditableTable } from '../componentes/participantes/ParticipanteEditableTable'
import {
  convertCidadaoGrupoItemModelToParticipanteEditableTableModel,
  convertLotacaoGrupoItemModelToLotacaoEditableTableModel,
} from '../converter-atividadeColetiva'
import {
  AtividadeColetivaFormModel,
  LOTACAO_ESTAGIO_SEM_PERMISSAO,
  praticaSaudeRecord,
  publicoAlvoRecord,
  temaSaudeRecord,
  tipoAtividadeRecord,
  TIPOS_ATIVIDADE_EM_SAUDE,
} from '../model-atividadeColetiva'
import {
  filterCidadaosFromGrupo,
  filterLotacoesFromGrupo,
  getMensagemGrupoAdicionado,
  getMensagemGrupoRemovido,
  isPse,
  removeCidadaosFromGrupo,
  removeLotacoesFromGrupo,
} from '../utils-atividadeColetiva'
import { validateLotacoes } from '../validator-atividadeColetivaCommonForm'
import { createAtividadeColetivaSaudeCalculations } from './calculator-atividadeColetivaSaude'
import { convertAtividadeColetivaSaudeFormModelToInput } from './converter-atividadeColetivaSaude'
import {
  getPraticasSaudeSubset,
  getTipoAtividadeSubset,
  isAtividadePseRequired,
  isCidadaosParticipantesRequired,
  isOnlyEducacaoPse,
  isPraticaSaudeDisabled,
  isPraticaSaudePnct,
  isPraticaSaudeRequired,
  isProcedimentoDisabled,
  isTemasSaudeRequired,
} from './utils-atividadeColetivaSaude'
import {
  atividadeColetivaSaudeFormValidator,
  validateDataNascimentoParticipantes,
  validateParticipantesObrigatorios,
} from './validator-atividadeColetivaSaudeForm'

const path = metaPath<AtividadeColetivaFormModel>()

const decorator = createDecorator(...createAtividadeColetivaSaudeCalculations(path))

type AtividadeColetivaSaudeFormProps = Pick<
  FormProps<AtividadeColetivaFormModel>,
  'initialValues' | 'initialDirtyFields'
> & {
  setCachedValue: Dispatch<SetStateAction<AtividadeColetivaFormModel>>
  clearCachedValue(): void
}

export default function AtividadeColetivaSaudeForm(props: AtividadeColetivaSaudeFormProps) {
  const { PILOTO_GRUPOS_ATIVIDADE_COLETIVA_ENABLED } = useFlags()

  const { setCachedValue, clearCachedValue, ...rest } = props
  const { initialValues } = rest
  const { getServerTimeNow } = useServerTime()
  const serverTime = getServerTimeNow()
  const history = useHistory()
  const alert = useAlert()
  const handleRejection = useErrorHandler()
  const { analytics } = useFirebase()
  const [salvarAtividadeColetiva] = useSalvarAtividadeColetivaMutation()
  const isEdicao = !!initialValues.id

  useEffect(() => () => clearCachedValue(), [clearCachedValue])

  if (
    initialValues.registroEnviado ||
    (initialValues.tipoAtividade && !TIPOS_ATIVIDADE_EM_SAUDE.includes(initialValues.tipoAtividade))
  ) {
    return <Redirect to='/404' />
  }

  const goBack = () => history.push(ATIVIDADE_COLETIVA_ATIVIDADE_SAUDE_PATH)

  const handleSubmit = (values: AtividadeColetivaFormModel) => {
    // TODO (Foundation): em #21794 será estudada uma melhor forma de apresentar esses erros
    const errorMessages = [
      validateLotacoes(values),
      validateParticipantesObrigatorios(values),
      validateDataNascimentoParticipantes(values),
    ].filterNotFalsy()

    if (errorMessages.isNotEmpty()) {
      errorMessages.forEach((error) => alert('danger', error))
    } else {
      return salvarAtividadeColetiva({
        variables: {
          input: convertAtividadeColetivaSaudeFormModelToInput(values, isPraticaSaudePnct(values.praticasSaude)),
        },
      })
        .then(() => {
          goBack()
          alert('success', 'Atividade coletiva salva com sucesso.')
          analytics.logEvent(`atividade_coletiva_saude_${values.id ? 'editada' : 'registrada'}`)
        })
        .catch(handleRejection)
    }
  }

  const handleChange = (formState: FormState<AtividadeColetivaFormModel>) => {
    setCachedValue({ ...formState.values })
  }

  const renderForm = (formProps: FormRenderProps<AtividadeColetivaFormModel>) => {
    const { values, submitting, handleSubmit } = formProps
    const actionRespAtividadeColetiva = values.lotacaoResponsavel.lotacao.cbo.actions.atividadeColetiva
    const mustShowProfissionaisAlert = values?.grupoParticipante && !isOnlyEducacaoPse(values)

    const handleAdicionarGrupo = (grupo: AtividadeColetivaGrupoItemModel) => {
      const currentParticipantes = values?.participantes ?? []
      const cidadaosFromGrupo = filterCidadaosFromGrupo(grupo.cidadaos, currentParticipantes)

      const currentLotacoesEnvolvidas = values?.lotacoesEnvolvidas ?? []
      const lotacoesFromGrupo = isOnlyEducacaoPse(values)
        ? []
        : filterLotacoesFromGrupo(grupo.lotacoes, currentLotacoesEnvolvidas)

      formProps.form.change(path.participantes.absolutePath(), [
        ...currentParticipantes,
        ...cidadaosFromGrupo.map((cidadao) => convertCidadaoGrupoItemModelToParticipanteEditableTableModel(cidadao)),
      ])

      formProps.form.change(path.lotacoesEnvolvidas.absolutePath(), [
        ...currentLotacoesEnvolvidas,
        ...lotacoesFromGrupo.map((lotacao) => convertLotacaoGrupoItemModelToLotacaoEditableTableModel(lotacao)),
      ])

      alert('success', getMensagemGrupoAdicionado(grupo.nome, lotacoesFromGrupo.length, cidadaosFromGrupo.length))
    }

    const handleRemoverGrupo = (grupo: AtividadeColetivaGrupoItemModel) => {
      const currentLotacoesEnvolvidas = values?.lotacoesEnvolvidas ?? []
      const lotacoesRestantes = removeLotacoesFromGrupo(currentLotacoesEnvolvidas, grupo.lotacoes)

      const currentParticipantes = values?.participantes ?? []
      const participantesRestantes = removeCidadaosFromGrupo(currentParticipantes, grupo.cidadaos)

      formProps.form.change(path.lotacoesEnvolvidas.absolutePath(), lotacoesRestantes)
      formProps.form.change(path.participantes.absolutePath(), participantesRestantes)

      alert(
        'success',
        getMensagemGrupoRemovido(
          grupo.nome,
          currentLotacoesEnvolvidas.length - lotacoesRestantes.length,
          currentParticipantes.length - participantesRestantes.length
        )
      )
    }

    return (
      <Grid gap={2}>
        <FormPrompt ignorePromptWhenSubmitting />
        <FormDebouncedValueSpy onChange={handleChange} />
        <Cell size={7}>
          <VFlow vSpacing={0.5}>
            <Heading level={3}>Responsabilidade de registro</Heading>
            <LotacaoResponsavelSection name={path.lotacaoResponsavel} isAtividadeColetiva />
            {!actionRespAtividadeColetiva.enabled && (
              <Alert type='danger' inline>
                {actionRespAtividadeColetiva.hint}
              </Alert>
            )}
            {isEstagio(values.lotacaoResponsavel.lotacao) && (
              <Alert type='danger' inline>
                {LOTACAO_ESTAGIO_SEM_PERMISSAO}
              </Alert>
            )}
          </VFlow>
        </Cell>
        <Cell size={5} />
        <Cell xs={3} lg={2} style={styles.section}>
          <DateField
            name={path.dataAtividade}
            label='Data da atividade'
            minDate={subMonths(serverTime, 12)}
            maxDate={serverTime}
            required
          />
        </Cell>

        <Cell size={2} style={styles.section}>
          <RecordStringSelectField name={path.turno} record={turnoRecord} label='Turno' required />
        </Cell>

        <Cell size={2} style={styles.section}>
          <NumberField
            name={path.numeroParticipantes}
            label='Nº de participantes'
            placeholder='Informe o número'
            min={1}
            max={999}
            maxLength={3}
            required
          />
        </Cell>

        <Cell size={3} style={styles.section}>
          <FormControl label='Programa saúde na escola' required={isAtividadePseRequired(values)}>
            <HFlow>
              <CheckboxField name={path.atividadePse.educacao} label='Educação' style={styles.checkbox} />
              <CheckboxField name={path.atividadePse.saude} label='Saúde' style={styles.checkbox} />
            </HFlow>
            <ErrorField style={styles.errorfield} name={path.atividadePse} />
          </FormControl>
        </Cell>

        <Cell size={12}>
          <LocalAtividadeField name={path.localAtividade} onlyInepEnabled={isPse(values)} />
        </Cell>

        {PILOTO_GRUPOS_ATIVIDADE_COLETIVA_ENABLED && !isEdicao && (
          <Cell size={12} style={styles.section}>
            <AtividadeColetivaGruposField
              name={path.grupoParticipante}
              tipoGrupo={TipoGrupoParticipanteAtividadeColetivaEnum.ATIVIDADE_EM_SAUDE}
              onAdicionarGrupo={handleAdicionarGrupo}
              onRemoverGrupo={handleRemoverGrupo}
            />
          </Cell>
        )}

        <Cell size={12} style={styles.section}>
          <HFlow hSpacing={0.2}>
            <Heading level={2} style={styles.title}>
              Profissionais envolvidos
            </Heading>
            {PILOTO_GRUPOS_ATIVIDADE_COLETIVA_ENABLED && mustShowProfissionaisAlert && (
              <Alert type='success' inline styles={{ wrapper: styles.alertWrapper }}>
                Profissionais do grupo {values.grupoParticipante.nome} foram adicionados à lista.
              </Alert>
            )}
          </HFlow>
          {/* TODO (Foundation): em #29795 será revisto o comportamento do alerta abaixo
          <Alert type='info' inline>
            A atividade será contabilizada na produção de todos os profissionais listados, sendo necessário apenas um
            registro.
          </Alert> */}
        </Cell>

        <Cell size={12}>
          {isOnlyEducacaoPse(values) ? (
            'Não é possível cadastrar profissionais para atividades de educação do Programa Saúde na Escola.'
          ) : (
            <LotacaoEditableTable
              name={path.lotacoesEnvolvidas}
              excludeProfissionaisIds={[
                values.lotacaoResponsavel?.lotacao?.profissional?.id,
                ...(values.lotacoesEnvolvidas?.map((item) => item?.lotacao?.profissional?.id) || []),
              ]}
            />
          )}
        </Cell>

        <Cell size={12} style={styles.section}>
          <Heading level={2} style={styles.title}>
            Dados da atividade
          </Heading>
        </Cell>

        <Cell size={4}>
          <RecordStringSelectField
            name={path.tipoAtividade}
            record={tipoAtividadeRecord}
            subset={getTipoAtividadeSubset(values)}
            label='Tipo da atividade'
            required
          />
        </Cell>

        <Cell size={8} />

        <Cell size={4}>
          <RecordStringSelectField
            name={path.publicoAlvo}
            record={publicoAlvoRecord}
            label='Público-alvo'
            required
            multiple
          />
        </Cell>

        <Cell size={4}>
          <RecordStringSelectField
            name={path.temasSaude}
            record={temaSaudeRecord}
            label='Temas para a saúde'
            required={isTemasSaudeRequired(values)}
            multiple
          />
        </Cell>

        <Cell size={4} />

        <Cell size={4}>
          <RecordStringSelectField
            name={path.praticasSaude}
            record={praticaSaudeRecord}
            subset={getPraticasSaudeSubset(values)}
            label='Praticas em saúde'
            required={isPraticaSaudeRequired(values)}
            disabled={isPraticaSaudeDisabled(values)}
            multiple
          />
        </Cell>

        <Cell size={4}>
          <ProcedimentoSelectField
            name={path.procedimento}
            label='Código do SIGTAP'
            tipoProcedimento={TipoProcedimentoEnum.GRUPO}
            dataAtendimento={dateAsYyyyMmDd(serverTime)}
            disabled={isProcedimentoDisabled(values)}
          />
        </Cell>

        <Cell size={12}>
          <RichTextField
            name={path.anotacoes}
            label='Anotações da atividade'
            placeholder='Insira as anotações da atividade'
            removeNonBreakingSpaces
            maxLength={4000}
            height={6}
            dataTestId='RichTextField.anotacoesAtividade'
          />
        </Cell>

        <Cell size={12} style={styles.section}>
          <HFlow hSpacing={0.2}>
            <Heading level={2} style={styles.title}>
              Participantes
            </Heading>
            {PILOTO_GRUPOS_ATIVIDADE_COLETIVA_ENABLED && values?.grupoParticipante && (
              <Alert type='success' inline styles={{ wrapper: styles.alertWrapper }}>
                Cidadãos do grupo {values.grupoParticipante.nome} foram adicionados à lista.
              </Alert>
            )}
          </HFlow>
        </Cell>

        <Cell size={12}>
          <ParticipanteEditableTable
            name={path.participantes}
            now={serverTime}
            profissionalResponsavel={values.lotacaoResponsavel?.lotacao?.profissional}
            profissionaisEnvolvidos={values.lotacoesEnvolvidas?.map((item) => item.lotacao?.profissional)}
            isPraticaSaudePnct={isPraticaSaudePnct(values?.praticasSaude)}
            dataAtividade={values.dataAtividade}
            tipoAtividade={values.tipoAtividade}
            required={isCidadaosParticipantesRequired(values?.tipoAtividade)}
          />
        </Cell>

        <Cell alignSelf='flex-end' size={12}>
          <DefaultFormFooter throttleSubmit loading={submitting} handleSubmit={handleSubmit} onCancel={goBack} />
        </Cell>
      </Grid>
    )
  }

  return (
    <Form<AtividadeColetivaFormModel>
      {...rest}
      render={renderForm}
      decorators={[decorator]}
      onSubmit={handleSubmit}
      validate={atividadeColetivaSaudeFormValidator(serverTime)}
    />
  )
}

const styles = {
  section: css`
    margin-top: 1.5rem;
  `,
  title: css`
    margin-bottom: 0.5rem;
  `,
  checkbox: css`
    margin-top: 0.25rem;
  `,
  errorfield: css`
    margin-top: 0.4rem;
  `,
  alertWrapper: css`
    background-color: transparent;
    border: none;
    margin-bottom: 0.2rem;
    padding-left: 0.2rem;
    word-break: break-word;
  `,
}
